
// calculates the weekly calls when given an array of every call, it cannot be given the pupils array it musy be an array where each item is the specific call containing submissionEpoch
export function calculateWeeklyCalls(records) {
    const startOfYear = new Date(new Date().getFullYear(), 8, 1);
    if (new Date() < startOfYear) startOfYear.setFullYear(startOfYear.getFullYear() - 1);

    const weeklyCalls = {};
    let weekStart = new Date(startOfYear);

    while (weekStart < new Date()) {
        const mondayDate = new Date(weekStart);
        mondayDate.setDate(mondayDate.getDate() - (mondayDate.getDay() - 1));
        const formattedDate = `${mondayDate.getDate().toString().padStart(2, '0')}/${(mondayDate.getMonth() + 1).toString().padStart(2, '0')}/${mondayDate.getFullYear()}`;
        weeklyCalls[formattedDate] = 0;  // Initialize count to 0
        weekStart.setDate(weekStart.getDate() + 7);  // Next week
    }

    records.forEach(record => {
        const callDate = new Date(record.submissionEpoch * 1000);
        callDate.setDate(callDate.getDate() - (callDate.getDay() - 1));  // Adjust to Monday
        const formattedDate = `${callDate.getDate().toString().padStart(2, '0')}/${(callDate.getMonth() + 1).toString().padStart(2, '0')}/${callDate.getFullYear()}`;
        weeklyCalls[formattedDate] = (weeklyCalls[formattedDate] || 0) + 1;
    });

    return weeklyCalls;
};


// finds the lesson/period name when given the epoch and the timetable timings from the system options table.
export function findSchoolPeriod(epoch, timetable) {

    // Convert the epoch to a Date object
    const date = new Date(epoch * 1000); // The epoch is in seconds, JavaScript Date expects milliseconds

    // Extract hours and minutes
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Convert time to minutes for easier comparison
    const timeInMinutes = hours * 60 + minutes;

    // Find the current period
    for (let i = 0; i < timetable.length; i++) {
        const periodStart = parseInt(timetable[i].start.split(':')[0]) * 60 + parseInt(timetable[i].start.split(':')[1]);
        const periodEnd = parseInt(timetable[i].end.split(':')[0]) * 60 + parseInt(timetable[i].end.split(':')[1]);

        if (timeInMinutes >= periodStart && timeInMinutes < periodEnd) {
            return timetable[i].period; // Returns the current period
        }
    }

    return "Outside school hours"; // If the time doesn't match any period
}


// Builds a stable list of call type options for report filters, preserving API order first and
// then appending any call types that only exist in the returned record data.
export function getOnCallReportCallTypeOptions(callTypes = [], records = []) {
    const orderedOptions = [];
    const seenNames = new Set();

    callTypes.forEach((callType) => {
        const typeName = String(callType?.typeName || '').trim();
        if (!typeName || seenNames.has(typeName)) {
            return;
        }

        seenNames.add(typeName);
        orderedOptions.push({
            typeName,
            colourCode: callType?.colourCode || '#54abcd',
        });
    });

    records.forEach((record) => {
        const typeName = String(record?.callType || '').trim();
        if (!typeName || seenNames.has(typeName)) {
            return;
        }

        seenNames.add(typeName);
        orderedOptions.push({
            typeName,
            colourCode: '#54abcd',
        });
    });

    return orderedOptions;
}


// Returns true when a call record matches the active report filters selected in the UI.
export function callMatchesOnCallReportFilters(callRecord, filters = {}, timetableTimings = []) {
    const hasAllowedCallTypeFilter = Array.isArray(filters?.allowedCallTypes);
    const allowedCallTypes = hasAllowedCallTypeFilter ? filters.allowedCallTypes : [];
    const hasAllowedLessonFilter = Array.isArray(filters?.allowedLessons);
    const allowedLessons = hasAllowedLessonFilter ? filters.allowedLessons : [];
    const selectedCallType = filters?.selectedCallType || null;

    if (hasAllowedCallTypeFilter && !allowedCallTypes.includes(callRecord?.callType)) {
        return false;
    }

    if (selectedCallType && callRecord?.callType !== selectedCallType) {
        return false;
    }

    if (hasAllowedLessonFilter) {
        const callPeriod = findSchoolPeriod(callRecord?.submissionEpoch, timetableTimings);
        if (!allowedLessons.includes(callPeriod)) {
            return false;
        }
    }

    return true;
}


// Chooses readable text for call type colour blocks using WCAG relative luminance.
export function getReadableTextColourForBackground(backgroundColour = '') {
    const cleanColour = String(backgroundColour || '').trim();
    const hexMatch = cleanColour.match(/^#?([a-f\d]{3}|[a-f\d]{6})$/i);
    const rgbMatch = cleanColour.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    let red = 242;
    let green = 242;
    let blue = 242;

    if (hexMatch) {
        const hexValue = hexMatch[1].length === 3
            ? hexMatch[1].split('').map((character) => character + character).join('')
            : hexMatch[1];
        red = parseInt(hexValue.slice(0, 2), 16);
        green = parseInt(hexValue.slice(2, 4), 16);
        blue = parseInt(hexValue.slice(4, 6), 16);
    } else if (rgbMatch) {
        red = Number(rgbMatch[1]);
        green = Number(rgbMatch[2]);
        blue = Number(rgbMatch[3]);
    }

    const toLinearRgb = (colourValue) => {
        const channel = colourValue / 255;
        return channel <= 0.03928
            ? channel / 12.92
            : ((channel + 0.055) / 1.055) ** 2.4;
    };
    const luminance = (0.2126 * toLinearRgb(red)) + (0.7152 * toLinearRgb(green)) + (0.0722 * toLinearRgb(blue));

    return luminance > 0.45 ? '#706f6f' : '#ffffff';
}


// Applies the active UI filters to a flat array of individual call records.
export function filterOnCallReportRecords(records = [], filters = {}, timetableTimings = []) {
    return records.filter((record) => callMatchesOnCallReportFilters(record, filters, timetableTimings));
}


// Applies the active UI filters to pupil summary data while preserving each pupil record shape.
export function filterOnCallReportPupilRecords(pupilRecords = [], filters = {}, timetableTimings = []) {
    const pupilGroupFilters = filters?.pupilGroups || {};
    const pupilGroupNames = ['FSM', 'ALN', 'MTT'];

    return pupilRecords.filter((pupilRecord) => (
        pupilGroupNames.every((groupName) => {
            const selectedValue = pupilGroupFilters?.[groupName] || 'all';

            if (selectedValue === 'yes') {
                return Boolean(pupilRecord?.[groupName]);
            }

            if (selectedValue === 'no') {
                return !Boolean(pupilRecord?.[groupName]);
            }

            return true;
        })
    )).map((pupilRecord) => ({
        ...pupilRecord,
        onCallRecords: filterOnCallReportRecords(pupilRecord?.onCallRecords || [], filters, timetableTimings),
    })).filter((pupilRecord) => (pupilRecord?.onCallRecords || []).length > 0);
}


// Formats a Date object into dd.mm.yyyy for export metadata.
export function formatOnCallExportCreatedDate(dateValue = new Date()) {
    const day = String(dateValue.getDate()).padStart(2, '0');
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const year = String(dateValue.getFullYear());

    return `${day}.${month}.${year}`;
}


// Formats an input date string into dd/mm/yyyy for export metadata display.
export function formatOnCallExportRangeDate(dateValue = '') {
    const cleanValue = String(dateValue || '').trim();
    if (!cleanValue) {
        return '';
    }

    const dateParts = cleanValue.split('-');
    if (dateParts.length !== 3) {
        return cleanValue;
    }

    return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
}


// Builds the same pupil summary data shape shown in the on-call report pupils table.
export function buildOnCallPupilSummaryRows(pupilRecords = [], callTypes = []) {
    return pupilRecords.map((record) => {
        const totals = callTypes.reduce((accumulator, callType) => ({
            ...accumulator,
            [callType.typeName]: (record?.onCallRecords || []).filter((call) => call?.callType === callType.typeName).length,
        }), { total: (record?.onCallRecords || []).length });

        return {
            id: record?.id || '',
            pupilName: record?.pupilName || '',
            FSM: Boolean(record?.FSM),
            ALN: Boolean(record?.ALN),
            MTT: Boolean(record?.MTT),
            totals,
        };
    });
}


// Returns a readable pupil group marker list for exports and non-icon display surfaces.
export function buildOnCallPupilGroupLabels(pupilRecord = {}) {
    return [
        pupilRecord?.FSM ? 'FSM' : '',
        pupilRecord?.ALN ? 'ALN' : '',
        pupilRecord?.MTT ? 'MTT' : '',
    ].filter(Boolean);
}


function escapeCsvCell(value) {
    const normalisedValue = value == null ? '' : String(value);
    return `"${normalisedValue.replace(/"/g, '""')}"`;
}


// Downloads a CSV file built in the browser from a 2D row array.
export function downloadCsvFromRows(fileName, rows = []) {
    const csvContent = rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(csvBlob);
    const downloadLink = document.createElement('a');

    downloadLink.href = objectUrl;
    downloadLink.download = fileName;
    downloadLink.click();
    downloadLink.remove();
    URL.revokeObjectURL(objectUrl);
}
